import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Bell, Check, AlertTriangle, AlertCircle, Info, Sparkles, Database, ShieldAlert } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { fmtDateTime } from "@/lib/format";

const SEV_COLOR = {
  info:    { cls: "text-blue-300",    Icon: Info },
  warning: { cls: "text-amber-300",   Icon: AlertTriangle },
  error:   { cls: "text-red-300",     Icon: AlertCircle },
  critical:{ cls: "text-red-400",     Icon: AlertCircle },
};
const CAT_ICON = {
  extension: Sparkles, backup: Database, security: ShieldAlert,
};

export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const ref = useRef(null);
  const navigate = useNavigate();

  async function loadCount() {
    try {
      const { data } = await api.get("/admin/notifications/unread-count");
      setCount(data.count || 0);
    } catch { /* silent — non-super-admins simply 403 */ }
  }
  async function loadItems() {
    const { data } = await api.get("/admin/notifications?limit=20");
    setItems(data || []);
  }
  useEffect(() => { loadCount(); const t = setInterval(loadCount, 60_000); return () => clearInterval(t); }, []);
  useEffect(() => {
    function onClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  async function toggle() {
    const next = !open; setOpen(next);
    if (next) await loadItems();
  }
  async function markRead(n) {
    await api.post(`/admin/notifications/${n.id}/read`);
    setItems(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
    loadCount();
    if (n.link) navigate(n.link);
    setOpen(false);
  }
  async function markAll() {
    await api.post("/admin/notifications/mark-all-read");
    setCount(0);
    setItems(prev => prev.map(x => ({ ...x, read: true })));
  }

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} data-testid="notif-bell"
        className="relative p-2 rounded-md border border-zinc-800 hover:bg-zinc-800 text-zinc-300">
        <Bell className="w-4 h-4" />
        {count > 0 && (
          <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-zinc-950 text-[10px] font-bold">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-12 w-80 max-h-96 overflow-y-auto bg-zinc-900 border border-zinc-800 rounded-md shadow-xl z-50" data-testid="notif-panel">
          <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between">
            <span className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Operational Alerts</span>
            {items.some(i => !i.read) && (
              <button onClick={markAll} className="text-[11px] text-blue-400 hover:text-blue-300" data-testid="notif-markall">
                Alle als gelesen
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <div className="p-6 text-center text-zinc-500 text-sm">Keine Benachrichtigungen.</div>
          ) : (
            <ul>
              {items.map(n => {
                const sev = SEV_COLOR[n.severity] || SEV_COLOR.info;
                const Icon = CAT_ICON[n.category] || sev.Icon;
                return (
                  <li key={n.id}
                    onClick={() => markRead(n)}
                    data-testid={`notif-item-${n.id}`}
                    className={`px-3 py-2 border-b border-zinc-800 last:border-b-0 cursor-pointer hover:bg-zinc-800/60 ${n.read ? "opacity-60" : ""}`}>
                    <div className="flex items-start gap-2">
                      <Icon className={`w-4 h-4 mt-0.5 ${sev.cls}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-zinc-100 font-medium leading-tight">{n.title}</div>
                        {n.body && <div className="text-xs text-zinc-400 mt-0.5">{n.body}</div>}
                        {n.tenant_name && <div className="text-[10px] text-zinc-500 font-mono mt-0.5">{n.tenant_name} · {n.tenant_slug}</div>}
                        <div className="text-[10px] text-zinc-600 font-mono mt-0.5">{fmtDateTime(n.created_at)}</div>
                      </div>
                      {!n.read && <Check className="w-3.5 h-3.5 text-zinc-600" />}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
